/**
 * Crew.tsx — the crew board as a standard CRM page (Ship 7, Aug 24 2026).
 *
 * Board (the daily surface), Stats (the comparison wall) and Audit (managers).
 * The Shout-Out wall retired Oct 12, 2026 — 2 posts in its last 90 days; the
 * team's social chatter lives on WhatsApp (Greg's call). The `.crew-app` skin
 * stays scoped to the two legacy tabs' subtrees.
 *
 * Tab is URL-backed via `?tab=…` (keys unchanged for deep-link compat; an old
 * ?tab=wall link falls back to the board).
 */
import { useTabParam } from "../lib/useTabParam";
import { useAuth } from "../context/AuthContext";
import { isCrewManager } from "../components/crew/crewAuth";
import { logNavClick } from "../lib/uiEvents";
import CrewBoard from "../components/crew/CrewBoard";
import LeaderboardWall from "../components/crew/LeaderboardWall";
import GoalsAudit from "../components/crew/GoalsAudit";

type Tab = "board" | "lbwall" | "audit";

const CREW_TABS: readonly Tab[] = ["board", "lbwall", "audit"];

export default function Crew() {
  const [tab, setTab] = useTabParam<Tab>("tab", "board", CREW_TABS);
  const { member } = useAuth();
  // Goals Audit is limited to TL / ASM / executives + super-users.
  const canAudit = isCrewManager(member);
  // Non-managers who deep-link to ?tab=audit fall back to the board.
  const effectiveTab: Tab = tab === "audit" && !canAudit ? "board" : tab;

  const tabs: Array<{ key: Tab; label: string }> = [
    { key: "board", label: "🏆 Board" },
    { key: "lbwall", label: "Stats" },
    ...(canAudit ? [{ key: "audit" as Tab, label: "Audit" }] : []),
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 pt-4">
        <div className="border-b border-slate-200">
          <nav className="flex gap-1 overflow-x-auto">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => { logNavClick(`crew_tab_${t.key}`); setTab(t.key); }}
                className={
                  "px-4 py-2 text-sm font-medium rounded-t -mb-px border-b-2 whitespace-nowrap " +
                  (effectiveTab === t.key
                    ? "text-bardin-teal border-bardin-teal"
                    : "text-slate-500 border-transparent hover:text-slate-700")
                }
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {effectiveTab === "board" && <CrewBoard />}

      {/* Legacy tabs — functional, unchanged, old skin scoped to their subtree. */}
      {effectiveTab === "lbwall" && (
        <div className="crew-app"><LeaderboardWall /></div>
      )}
      {effectiveTab === "audit" && canAudit && (
        <div className="crew-app"><GoalsAudit /></div>
      )}
    </div>
  );
}
